
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Quest } from '@/types/quests';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { formSchema, FormValues, getDefaultValues } from './schema/QuestFormSchema';
import QuestBasicInfoFields from './form/QuestBasicInfoFields';
import QuestRewardsFields from './form/QuestRewardsFields';
import QuestStatusField from './form/QuestStatusField';
import { useBadgesList } from './hooks/useBadgesList';
import { useQuestForm } from './hooks/useQuestForm';

interface QuestDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  quest: Quest | null;
}

const QuestDialog: React.FC<QuestDialogProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  quest 
}) => {
  const { t } = useTranslation(['admin', 'quests', 'common']);
  const { badges } = useBadgesList();
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: getDefaultValues()
  });

  const { saving, formError, handleSubmit } = useQuestForm({ quest, onSave });

  // Reset form when the quest changes
  useEffect(() => {
    if (quest) {
      form.reset({
        name: quest.name,
        description: quest.description,
        points_awarded: quest.points_awarded,
        type: quest.type,
        badge_id: quest.badge_id || null,
        is_active: quest.is_active,
        end_date: quest.end_date || null,
      });
    } else {
      form.reset(getDefaultValues());
    }
  }, [quest, form]);

  const onSubmit = (values: FormValues) => {
    handleSubmit(values);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {quest ? t('admin:editQuest') : t('admin:newQuest')}
          </DialogTitle>
          <DialogDescription>
            {t('admin:questFormDescription')}
          </DialogDescription>
        </DialogHeader>
        
        {formError && (
          <Alert variant="destructive">
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 py-4">
            <QuestBasicInfoFields form={form} />
            
            <QuestRewardsFields form={form} badges={badges} />
            
            <QuestStatusField form={form} />
            
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={saving}
              >
                {t('common:cancel')}
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                aria-busy={saving}
              >
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {quest ? t('common:save') : t('common:create')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default QuestDialog;
